import { useState, useEffect, useMemo, memo } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import ReactGA from 'react-ga4';
import './App.css';
import './styles/header.css';
import Header from './components/Header';
import PriceChart from './components/PriceChart';
import SEO from './components/SEO';
import AboutUs from './pages/AboutUs';
import FranchiseInquiry from './pages/FranchiseInquiry';
import GoldProducts from './pages/Products';
import StoreLocator from './pages/StoreLocator';
import MarketPrice from './pages/MarketPrice';
import NotFound from './pages/NotFound';
import AdminPanel from './pages/AdminPanel';
import { stores } from './data/storeData';
import { PRODUCT_DATA } from './data/productsData';

// Google Analytics 초기화
ReactGA.initialize('G-92PL47KNNG'); // TODO: 실제 GA4 측정 ID로 교체

const bannerImages = [
  { type: 'image', src: '/images/main1.jpg' },
  { type: 'image', src: '/images/main2.jpg' },
  { type: 'image', src: '/images/main3.jpg' },
  { type: 'image', src: '/images/main4.jpg' }
];

const homeProducts = [
  ...PRODUCT_DATA.goldBars.slice(0, 2),
  ...PRODUCT_DATA.luckyKeys,
  ...PRODUCT_DATA.jewelry.slice(0, 2),
  ...PRODUCT_DATA.animals
].slice(0, 8);

// Store 아이템 컴포넌트 메모이제이션
const StoreItem = memo(({ store }) => (
  <div className="store-showcase__item">
    <div 
      className="store-showcase__image" 
      style={{backgroundImage: `url(${store.images.main})`}}
      loading="lazy"
    />
    <h5 className="store-showcase__name">{store.name}</h5>
    <p className="store-showcase__address">
      {store.address.split(' ').slice(0, 3).join(' ')}
    </p>
  </div>
));

const PriceCell = ({ data }) => {
  if (!data) return <span className="market-table__unchanged">제품시세적용</span>;
  
  const isNegative = data.change < 0;
  const absChange = Math.abs(data.change);
  
  return (
    <>
      <span className="market-table__price">{Number(data.price).toLocaleString()}</span>
      <p className="market-table__change">
        {data.change === 0 ? (
          <span>-</span>
        ) : (
          <>
            <span className="market-table__percent">{isNegative ? '-' : ''}{Math.abs(data.changePer)}%</span>
            <span className={isNegative ? 'market-table__change--down' : 'market-table__change--up'}>
              {isNegative ? '▼' : '▲'} {absChange.toLocaleString()}
            </span>
          </>
        )}
      </p>
    </>
  );
};

const PriceRow = ({ label, subLabel, buyData, sellData }) => (
  <tr>
    <th>
      {label}<b className="market-table__sub">{subLabel}</b>
    </th>
    <td><PriceCell data={buyData} /></td>
    <td><PriceCell data={sellData} /></td>
  </tr>
);

function HomePage() {
  const [currentBanner, setCurrentBanner] = useState(0);
  const [priceData, setPriceData] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const [storeOffset, setStoreOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [trackWidth, setTrackWidth] = useState(0);
  const [isStoreVisible, setIsStoreVisible] = useState(false);

  useEffect(() => {
    fetch('https://koreakookmingold.com/api/today-price')
      .then(res => res.json())
      .then(data => setPriceData(data))
      .catch(err => console.error('가격 데이터 로드 실패:', err));
  }, []);

  useEffect(() => {
    const bannerInterval = setInterval(() => {
      if (!isHovering) {
        setCurrentBanner(prev => (prev + 1) % bannerImages.length);
      }
    }, bannerImages[currentBanner]?.type === 'video' ? 15000 : 6000);
    return () => clearInterval(bannerInterval);
  }, [isHovering, currentBanner]);

  useEffect(() => {
    const videos = document.querySelectorAll('.hero-banner__slide video');
    videos.forEach((video, index) => {
      video.pause();
      if (index === currentBanner) {
        video.currentTime = 0;
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {});
        }
      }
    });
  }, [currentBanner]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle('animate-in', entry.isIntersecting);
          if (entry.target.classList.contains('store-showcase')) {
            setIsStoreVisible(entry.isIntersecting);
            if (entry.isIntersecting && !trackWidth) {
              setStoreOffset(0);
              const track = entry.target.querySelector('.store-showcase__track');
              if (track) setTrackWidth(track.scrollWidth);
            }
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isDragging || !trackWidth || !isStoreVisible) return;
    const interval = setInterval(() => {
      setStoreOffset(prev => {
        if (prev <= -(trackWidth - 320)) return 0;
        return prev - 1;
      });
    }, 20);
    return () => clearInterval(interval);
  }, [isDragging, trackWidth, isStoreVisible]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX || e.touches[0].pageX);
    setScrollLeft(storeOffset);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !trackWidth) return;
    const x = e.pageX || e.touches?.[0]?.pageX;
    if (!x) return;
    const walk = (x - startX) * 2;
    const newOffset = scrollLeft + walk;
    setStoreOffset(Math.max(-(trackWidth - 320), Math.min(0, newOffset)));
  };

  const stopDragging = () => setIsDragging(false);

  // 표시할 store 데이터 메모이제이션
  const displayStores = useMemo(() => stores, []);



  return (
    <>
      <SEO />
      <main className="home">
      <section className="home__section home__section--hero">
        <div className="hero-banner">
          <div className="hero-banner__content">
            <div className="hero-banner__slider">
              {bannerImages.map((item, index) => (
                <div key={index} className={`hero-banner__slide ${index === currentBanner ? 'hero-banner__slide--active' : ''}`}>
                  {item.type === 'video' ? (
                    <video 
                      src={item.src} 
                      muted 
                      playsInline
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <img src={item.src} alt="배너" />
                  )}
                </div>
              ))}
              <div className="hero-banner__indicators" onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>
                {bannerImages.map((_, index) => (
                  <button
                    key={index}
                    className={`hero-banner__indicator ${index === currentBanner ? 'hero-banner__indicator--active' : ''}`}
                    onClick={() => setCurrentBanner(index)}
                    onMouseEnter={() => setCurrentBanner(index)}
                    aria-label={`배너 ${index + 1}`}
                  />
                ))}
              </div>
            </div>
            <div className="market-table">
              <div className="market-table__inner">
              <table className="market-table__table">
                <caption className="market-table__caption">
                  한국국민금거래소 시세
                  {priceData?.date && <span className="market-table__time">{priceData.date.split(' ')[0]}</span>}
                </caption>
                <colgroup>
                  <col />
                  <col />
                  <col />
                </colgroup>
                <thead>
                  <tr>
                    <th></th>
                    <th>내가 살 때</th>
                    <th>내가 팔 때</th>
                  </tr>
                </thead>
                <tbody className="default">
                  <PriceRow 
                    label="24K 금" 
                    subLabel="Gold24k-3.75g"
                    buyData={priceData?.sell24k && {
                      price: priceData.sell24k,
                      change: priceData.diffSell24k,
                      changePer: priceData.pctSell24k
                    }}
                    sellData={priceData?.buy24k && {
                      price: priceData.buy24k,
                      change: priceData.diffBuy24k,
                      changePer: priceData.pctBuy24k
                    }}
                  />
                  <PriceRow 
                    label="18K 금" 
                    subLabel="Gold18k-3.75g"
                    buyData={priceData?.sell18k && {
                      price: priceData.sell18k,
                      change: priceData.diffSell18k,
                      changePer: priceData.pctSell18k
                    }}
                    sellData={priceData?.buy18k && {
                      price: priceData.buy18k,
                      change: priceData.diffBuy18k,
                      changePer: priceData.pctBuy18k
                    }}
                  />
                  <PriceRow 
                    label="14K 금" 
                    subLabel="Gold14k-3.75g"
                    buyData={priceData?.sell14k && {
                      price: priceData.sell14k,
                      change: priceData.diffSell14k,
                      changePer: priceData.pctSell14k
                    }}
                    sellData={priceData?.buy14k && {
                      price: priceData.buy14k,
                      change: priceData.diffBuy14k,
                      changePer: priceData.pctBuy14k
                    }}
                  />
                  <PriceRow 
                    label="백금" 
                    subLabel="Platinum-3.75g"
                    buyData={priceData?.sellWhite && {
                      price: priceData.sellWhite,
                      change: priceData.diffSellWhite,
                      changePer: priceData.pctSellWhite
                    }}
                    sellData={priceData?.buyWhite && {
                      price: priceData.buyWhite,
                      change: priceData.diffBuyWhite,
                      changePer: priceData.pctBuyWhite
                    }}
                  />
                  <PriceRow 
                    label="은" 
                    subLabel="Silver-3.75g"
                    buyData={priceData?.sellSilver && {
                      price: priceData.sellSilver,
                      change: priceData.diffSellSilver,
                      changePer: priceData.pctSellSilver
                    }}
                    sellData={priceData?.buySilver && {
                      price: priceData.buySilver,
                      change: priceData.diffBuySilver,
                      changePer: priceData.pctBuySilver
                    }}
                  />
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="home__section home__section--chart">
        <div className="home__container">
          <div className="home__header animate-on-scroll">
            <h3 className="home__title">귀금속 시세 차트</h3>
            <p className="home__description">실시간 금, 은 시세 동향을 확인하세요.</p>
          </div>
          <div className="price-graph animate-on-scroll">
            <PriceChart metalType="gold" title="금 시세" unit="KRW/3.75g" />
            <PriceChart metalType="silver" title="은 시세" unit="KRW/3.75g" />
          </div>
        </div>
      </section>

      <section className="home__section home__section--stores">
        <div className="home__container">
          <div className="home__header animate-on-scroll animate-in" style={{ marginBottom: '30px' }}>
            <video 
              src="/videos/jamsil-video.mp4" 
              autoPlay
              muted 
              loop
              playsInline
            />
          </div>
          {/* <div className="home__header animate-on-scroll" style={{ marginBottom: '30px' }}>
            <a href="/store-locator" className="home__button">가까운 지점 찾기</a>
          </div> */}
          
          {/* <div 
            className="store-showcase animate-on-scroll"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={stopDragging}
            onMouseLeave={stopDragging}
            onTouchStart={handleMouseDown}
            onTouchMove={handleMouseMove}
            onTouchEnd={stopDragging}
          >
            <div 
              className="store-showcase__track" 
              style={{ 
                transform: `translateX(${storeOffset}px)`,
                cursor: isDragging ? 'grabbing' : 'grab'
              }}
            >
              {displayStores.map((store) => (
                <StoreItem key={store.id} store={store} />
              ))}
            </div>
          </div> */}
        </div>
      </section>

      <section className="home__section home__section--about">
        <div className="home__container">
          <div className="home__header animate-on-scroll">
            <h2 className="home__title">
              대한민국 최고의<br />
              <span className="home__title--highlight">금거래 전문기업</span>
            </h2>
          </div>
          
          <div className="service-list animate-on-scroll">
            <div className="service-list__item">
              <div className="service-list__icon">
                <img src="/images/icon-1.webp" alt="금매입" className="service-list__image" />
              </div>
              <h3 className="service-list__title">금매입</h3>
              <p className="service-list__description">순금, 18K, 14K, 백금, 은 치금 등<br />최고가 시세로 매입</p>
            </div>
            <div className="service-list__item">
              <div className="service-list__icon">
                <img src="/images/icon-2.webp" alt="다이아몬드" className="service-list__image" />
              </div>
              <h3 className="service-list__title">다이아몬드 매입</h3>
              <p className="service-list__description">우신, 현대, GIA 등 감정서가 없는<br />다이아도 무료 감정 후 고가 매입</p>
            </div>
            <div className="service-list__item">
              <div className="service-list__icon">
                <img src="/images/icon-3.webp" alt="명품시계" className="service-list__image" />
              </div>
              <h3 className="service-list__title">중고명품시계 매입</h3>
              <p className="service-list__description">중고명품시계<br />고가 매입</p>
            </div>
          </div>
        </div>
      </section>

      <section className="home__section home__section--features">
        <div className="home__container">
          <div className="home__header animate-on-scroll">
            <h3 className="home__title">한국국민금거래소 제품을 소개합니다</h3>
          </div>
          <div className="product-grid animate-on-scroll">
            {homeProducts.map((product, index) => (
              <div key={index} className="product-grid__item">
                <div className="product-grid__image" style={{backgroundImage: `url(${product.image})`}}></div>
                <h5 className="product-grid__name">{product.name}</h5>
              </div>
            ))}
          </div>
          <div className="home__footer animate-on-scroll">
            <a href="/products" className="home__button">전체 제품보기</a>
          </div>
        </div>
      </section>
    </main>
    </>
  );
}

function AppContent() {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 페이지 변경 시 자동 추적
  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: location.pathname, title: document.title });
  }, [location.pathname]);

  return (
    <div className="App">
      <Header scrolled={scrolled} />
    
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutUs />} />
        {/* <Route path="/franchise" element={<FranchiseInquiry />} /> */}
        <Route path="/products" element={<GoldProducts />} />
        <Route path="/store-locator" element={<StoreLocator />} />
        <Route path="/market-price" element={<MarketPrice />} />
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="*" element={<NotFound />} />
      </Routes>

      <footer className="footer">
        <div className="footer__container">
          <div className="footer__logo">한국국민금거래소</div>
          <div className="footer__info">
            <strong>한국국민금거래소</strong> : 서울시 송파구 양재대로 1222 올림픽프라자 1층 88호 (방이동 올림픽선수촌)<br/>
            <strong>TEL</strong> : 02-409-7800 <strong>E-mail</strong> : jglee010@gmail.com<br/>
            <strong>상호</strong> : 한국국민금거래소 | <strong>대표</strong> : 이종기 | <strong>사업자등록</strong> : 767-04-02996
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <AppContent />
      </Router>
    </HelmetProvider>
  );
}

export default App;